// Client-safe leaf, exported as `@mentora/data-access/analytics-events`: no DB,
// no env, no node imports. The browser SDK and the server module both type
// their calls against these maps, so an unknown event or a missing property is
// a compile error at the call site instead of a silent hole in a funnel.
import type { StorefrontId } from '../auth/storefront-id'
import { z } from 'zod'

export type { StorefrontId }

/** List currency per storefront, for client events (payments.ts is a client-safe leaf). */
export { STOREFRONT_CURRENCY } from '../payments'

/** A signed-in user's answer to the analytics banner, as stored on their user row (null: never answered while signed in). */
export const analyticsConsentSchema = z.enum(['granted', 'denied'])
export type AnalyticsConsent = z.infer<typeof analyticsConsentSchema>

type Cents = number
/** Lowercase ISO 4217, the form used by `STOREFRONT_CURRENCY` and the purchase rows. */
type Currency = string
type PurchaseKind = 'course' | 'event_ticket'
type EventCheckoutSource = 'ticket_panel' | 'cbcp_campaign'

export interface ClientEvents {
  course_viewed: { course_id: string, org_slug: string, price_cents: Cents, currency: Currency, is_free: boolean }
  checkout_started: { course_id: string, price_cents: Cents, currency: Currency, coupon_applied: boolean }
  checkout_currency_switched: { course_id: string, to_currency: Currency }
  coupon_applied: { course_id: string }
  coupon_rejected: { course_id: string, reason: string }
  // `currency` null: cancelled on the ASCPS card-country step, before one was chosen.
  checkout_cancelled: { course_id: string, currency: Currency | null }
  event_viewed: { event_id: string, event_slug: string, tier_count: number }
  event_tier_selected: { event_id: string, event_slug: string, tier_id: string, tier_eligibility: string }
  // `source`: the ticket panel, or the CBCP campaign's membership-bundle form on the congress page.
  event_checkout_started: {
    event_id: string
    event_slug: string
    tier_id: string
    tier_eligibility: string
    price_cents: Cents
    currency: Currency
    source: EventCheckoutSource
  }
  event_checkout_cancelled: { event_id: string, event_slug: string, tier_id: string, currency: Currency | null, source: EventCheckoutSource }
  event_coupon_applied: { event_id: string, event_slug: string, tier_id: string }
  event_coupon_rejected: { event_id: string, event_slug: string, reason: string }
  // No price: the sheet never sees it — the server prices the subscription.
  membership_checkout_started: Record<string, never>
  signup_submitted: { method: 'email' | 'google' }
  lesson_video_started: { course_id: string, lesson_id: string }
  lesson_completed: { course_id: string, lesson_id: string, duration_seconds: number }
  // Names only, never values (a search term is free text). No result count:
  // the controls change the URL and the server renders the results.
  catalogue_filtered: { filter_keys: string[] }
}

export interface ServerEvents {
  user_signed_up: Record<string, never>
  purchase_completed: {
    kind: PurchaseKind
    item_id: string
    provider: string
    amount_cents: Cents
    currency: Currency
    charged_currency: Currency | null
    is_free: boolean
    coupon_used: boolean
    $revenue: number
    /** The course or event slug — what a dashboard breakdown shows; null if the lookup failed. */
    item_slug: string | null
    /** Event tickets only (null for courses). */
    tier_id: string | null
    /** `general` | `ascps_member` | `partner_member` | `membership_bundle`; null for courses. */
    tier_eligibility: string | null
  }
  // One per refund, partial ones included. `amount_cents` is what this refund
  // returned, in list currency; `revoked` whether access went with it.
  purchase_refunded: {
    kind: PurchaseKind
    item_id: string
    origin: 'buyer_request' | 'admin_request' | 'provider_notified'
    amount_cents: Cents
    currency: Currency
    revoked: boolean
  }
  // No amount: the membership row does not record what was paid (the price is
  // a mutable setting, and the congress bundle sells it at its own price).
  membership_activated: Record<string, never>
  membership_status_changed: { from: string, to: string }
}

/** Auth users are isolated per storefront schema (ADR-001), so their ids are too. */
export function analyticsDistinctId(storefrontId: StorefrontId, userId: string): string {
  return `${storefrontId}:${userId}`
}

/** PostHog's `$revenue` is in major units; the money record stays in cents. */
export function centsToRevenue(cents: Cents): number {
  return cents / 100
}
